import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AutoActiveProvider } from './AutoActiveContext.js';
import { ComponentsProvider } from './ComponentsContext.js';
import { ElementRenderer } from './ElementRenderer.js';
import { InteractionProvider, useInteraction } from './InteractionContext.js';
import { toNumericPx } from './layout.js';
import { LayoutsProvider, useLayoutDef } from './LayoutsContext.js';
import { cn } from './lib/utils.js';
import { ZoomProvider } from './ZoomContext.js';
// Default viewport dimensions for each screen type
const DEFAULT_VIEWPORT_SIZES = {
    mobile: { width: 375, height: 667 },
    tablet: { width: 768, height: 1024 },
    desktop: { width: 1280, height: 800 },
};
/**
 * Main renderer component for WireScript documents
 */
export function WireRenderer({ document, screenId, viewport, zoom = 1, onScreenChange, }) {
    // Find initial screen to render - fall back to first screen if screenId not found
    const initialScreen = screenId
        ? document.screens.find((s) => s.id === screenId) || document.screens[0]
        : document.screens[0];
    if (!initialScreen) {
        return _jsx("div", { className: "p-6 text-destructive", children: "No screens defined" });
    }
    // Clamp zoom to valid range (0.1 to 3)
    const clampedZoom = Math.min(Math.max(zoom, 0.1), 3);
    // Collect initially open overlays (those with :open flag)
    const initialOpenOverlays = document.screens.flatMap((screen) => screen.overlays.filter((overlay) => overlay.props.open === true).map((overlay) => overlay.id));
    // Calculate dimensions for zoom container
    const screenViewport = initialScreen.viewport || 'desktop';
    const defaultSize = DEFAULT_VIEWPORT_SIZES[screenViewport];
    const contentWidth = viewport && typeof viewport.width === 'number' && viewport.width > 0
        ? viewport.width
        : defaultSize.width;
    const contentHeight = viewport && typeof viewport.height === 'number' && viewport.height > 0
        ? viewport.height
        : defaultSize.height;
    const content = (_jsx(ZoomProvider, { zoom: clampedZoom, children: _jsx(InteractionProvider, { initialScreen: initialScreen.id, initialOpenOverlays: initialOpenOverlays, onScreenChange: onScreenChange, children: _jsx(ComponentsProvider, { components: document.components, children: _jsx(LayoutsProvider, { layouts: document.layouts, children: _jsx(DocumentRenderer, { document: document, viewport: viewport }) }) }) }) }));
    // No transformation needed when zoom is 1
    if (clampedZoom === 1) {
        return content;
    }
    // Apply transform: scale() with proper container sizing
    // Use minHeight to allow content to grow beyond viewport height
    return (_jsx("div", { style: {
            width: contentWidth * clampedZoom,
            minHeight: contentHeight * clampedZoom,
            overflow: 'visible',
        }, children: _jsx("div", { style: {
                transformOrigin: 'top left',
                transform: `scale(${clampedZoom})`,
                width: contentWidth,
                minHeight: contentHeight,
            }, children: content }) }));
}
/**
 * Internal component that renders the current screen from context
 */
function DocumentRenderer({ document, viewport }) {
    const { state } = useInteraction();
    const currentScreenId = state.currentScreen;
    // Find current screen from document - fall back to first screen if not found
    const screen = currentScreenId
        ? document.screens.find((s) => s.id === currentScreenId) || document.screens[0]
        : document.screens[0];
    if (!screen) {
        return _jsx("div", { className: "p-6 text-destructive", children: "No screens defined" });
    }
    return _jsx(ScreenRenderer, { screen: screen, viewport: viewport });
}
/**
 * Recursively replace (slot) elements with the replacement content.
 */
function replaceSlot(node, replacement) {
    // If this is the slot, return the replacement
    if (node.elementType === 'slot') {
        return replacement;
    }
    // Otherwise, recursively process children
    return {
        ...node,
        children: node.children.map((child) => {
            if (child.type === 'element') {
                return replaceSlot(child, replacement);
            }
            if (child.type === 'repeat') {
                return {
                    ...child,
                    body: replaceSlot(child.body, replacement),
                };
            }
            return child;
        }),
    };
}
/**
 * Renders a single screen
 */
export function ScreenRenderer({ screen, viewport: externalViewport }) {
    const { isOverlayOpen, closeOverlay } = useInteraction();
    const containerRef = useRef(null);
    const [portalContainer, setPortalContainer] = useState(null);
    // Get layout if screen references one
    const layoutDef = useLayoutDef(screen.layout || '');
    // Expand layout with screen content, or use screen root directly
    const contentToRender = useMemo(() => {
        if (layoutDef) {
            return replaceSlot(layoutDef.body, screen.root);
        }
        return screen.root;
    }, [layoutDef, screen.root]);
    // Default viewport dimensions based on screen flag
    const defaultSizes = {
        mobile: { width: 375, minHeight: 667 },
        tablet: { width: 768, minHeight: 1024 },
        desktop: { width: 1280, minHeight: 800 },
    };
    const screenViewport = screen.viewport || 'desktop';
    const defaultSize = defaultSizes[screenViewport];
    // Use external viewport if provided (numeric value), otherwise use defaults
    // 'auto' means fill container width/height
    const useExternalWidth = externalViewport && typeof externalViewport.width === 'number' && externalViewport.width > 0;
    const useExternalHeight = externalViewport && typeof externalViewport.height === 'number' && externalViewport.height > 0;
    const useAutoWidth = externalViewport?.width === 'auto';
    const useAutoHeight = externalViewport?.height === 'auto';
    const containerStyle = {
        width: useAutoWidth ? '100%' : useExternalWidth ? externalViewport.width : defaultSize.width,
        minHeight: useAutoHeight ? '100%' : useExternalHeight ? externalViewport.height : defaultSize.minHeight,
        height: useAutoHeight ? '100%' : useExternalHeight ? externalViewport.height : 'auto',
        // Never add internal scroll - let parent container handle scrolling
        overflow: 'hidden',
    };
    // Set up portal container for overlays (renders at screen container level but visually full)
    useEffect(() => {
        if (containerRef.current) {
            setPortalContainer(containerRef.current);
        }
    }, []);
    return (_jsx(AutoActiveProvider, { screenId: screen.id, children: _jsxs("div", { ref: containerRef, className: "relative bg-background text-foreground font-sans", style: containerStyle, children: [_jsx(ElementRenderer, { node: contentToRender }), screen.overlays.map((overlay) => {
                    const isOpen = isOverlayOpen(overlay.id);
                    const overlayElement = (_jsx(OverlayRenderer, { overlay: overlay, isOpen: isOpen, onClose: () => closeOverlay(overlay.id), containerWidth: containerStyle.width, containerHeight: typeof containerStyle.height === 'number' ? containerStyle.height : undefined }, overlay.id));
                    // Use portal on client, render directly on server
                    return portalContainer ? createPortal(overlayElement, portalContainer) : overlayElement;
                })] }) }));
}
/**
 * Renders an overlay (modal, drawer, dropdown, popover)
 * Uses fixed positioning relative to viewport for proper full-screen coverage
 */
function OverlayRenderer({ overlay, isOpen, onClose, containerWidth, containerHeight, }) {
    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const isDrawer = overlay.overlayType === 'drawer';
    const isLeftDrawer = isDrawer && overlay.props.left === true;
    const isModal = overlay.overlayType === 'modal';
    const isDropdown = overlay.overlayType === 'popover';
    // Calculate overlay dimensions
    const drawerWidth = toNumericPx(overlay.props.width) ?? 400;
    const maxModalWidth = containerWidth ? Math.min(containerWidth * 0.9, 600) : 600;
    return (_jsx("div", { className: cn('absolute inset-0 bg-black/50 flex items-center z-50', isDrawer && isLeftDrawer && 'justify-start', isDrawer && !isLeftDrawer && 'justify-end', !isDrawer && 'justify-center'), style: {
            minHeight: containerHeight || '100%',
            display: isOpen ? 'flex' : 'none',
        }, onClick: handleBackdropClick, children: _jsx("div", { className: cn('relative bg-popover text-popover-foreground border border-border overflow-auto', isDrawer && 'h-full max-h-full rounded-none', isModal && 'rounded-lg', isDropdown && 'rounded-md min-w-[200px]'), style: {
                width: isDrawer ? Math.min(drawerWidth, containerWidth || drawerWidth) : undefined,
                maxWidth: !isDrawer ? maxModalWidth : undefined,
                maxHeight: !isDrawer ? (containerHeight ? containerHeight * 0.9 : '90%') : undefined,
            }, onClick: (e) => e.stopPropagation(), children: overlay.children.map((child, i) => (_jsx(ElementRenderer, { node: child }, `overlay-${overlay.id}-${i}`))) }) }));
}
/**
 * Get all screen IDs from a document
 */
export function getScreenIds(document) {
    return document.screens.map((s) => s.id);
}
/**
 * Get screen info
 */
export function getScreenInfo(document, screenId) {
    const screen = document.screens.find((s) => s.id === screenId);
    if (!screen)
        return null;
    return {
        id: screen.id,
        name: screen.name,
        viewport: screen.viewport || 'desktop',
        overlayCount: screen.overlays.length,
    };
}
//# sourceMappingURL=WireRenderer.js.map